import React, { useState, useEffect, useCallback } from "react";
import settingsService from "../services/settingsService";
import DataGrid, {
  Column,
  Editing,
  Paging,
  Pager,
  Item,
  Popup,
  Form
} from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import api from "../services/axiosInstance";

function ToggleSwitch({ id, checked, onChange, disabled }) {
  return (
    <label
      htmlFor={id}
      style={{
        position: "relative",
        display: "inline-block",
        width: "44px",
        height: "24px",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        style={{ opacity: 0, width: 0, height: 0, position: "absolute" }}
      />
      <span
        style={{
          position: "absolute",
          inset: 0,
          backgroundColor: checked ? "var(--brand-light)" : "var(--border-strong)",
          borderRadius: "24px",
          transition: "background-color 0.2s ease",
        }}
      />
      <span
        style={{
          position: "absolute",
          top: "3px",
          left: checked ? "23px" : "3px",
          width: "18px",
          height: "18px",
          backgroundColor: "#fff",
          borderRadius: "50%",
          transition: "left 0.2s ease",
          boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
        }}
      />
    </label>
  );
}


function SettingRow({ label, description, id, checked, onChange, disabled, badge }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: "24px",
        padding: "18px 0",
        borderBottom: "1px solid var(--border-color)",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
          <span
            style={{
              fontSize: "var(--fs-base)",
              fontWeight: 600,
              color: "var(--text-color)",
            }}
          >
            {label}
          </span>
          {badge && (
            <span
              style={{
                fontSize: "var(--fs-xs)",
                fontWeight: 600,
                padding: "1px 7px",
                borderRadius: "var(--radius-sm)",
                background: "var(--accent-soft)",
                color: "var(--brand-light)",
                border: "1px solid var(--accent-glow)",
                letterSpacing: "0.3px",
              }}
            >
              {badge}
            </span>
          )}
        </div>
        <p
          style={{
            fontSize: "var(--fs-sm)",
            color: "var(--text-muted)",
            margin: 0,
            lineHeight: 1.5,
          }}
        >
          {description}
        </p>
      </div>
      <div style={{ flexShrink: 0, paddingTop: "2px" }}>
        <ToggleSwitch id={id} checked={checked} onChange={onChange} disabled={disabled} />
      </div>
    </div>
  );
}


function StatusBanner({ type, message }) {
  const colors = {
    success: { bg: "var(--success-soft)", border: "rgba(22,163,74,0.2)", text: "var(--success)" },
    error:   { bg: "var(--error-soft)",   border: "rgba(220,38,38,0.2)",  text: "var(--error)"   },
    info:    { bg: "var(--info-soft)",     border: "rgba(37,99,235,0.2)",  text: "var(--info)"    },
  };
  const c = colors[type] || colors.info;

  return (
    <div
      role="alert"
      style={{
        padding: "10px 14px",
        borderRadius: "var(--radius-md)",
        background: c.bg,
        border: `1px solid ${c.border}`,
        color: c.text,
        fontSize: "var(--fs-sm)",
        fontWeight: 500,
        display: "flex",
        alignItems: "center",
        gap: "8px",
        animation: "toast-in 0.2s ease",
      }}
    >
      {type === "success" && (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
      {type === "error" && (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      )}
      {message}
    </div>
  );
}


export default function Settings() {
  const [activeTab, setActiveTab] = useState("general");
  const [allowAutoCreate, setAllowAutoCreate] = useState(true);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [status, setStatus]     = useState(null); // { type, message }

  // Telegram settings state
  const [telegramEnabled, setTelegramEnabled] = useState(false);
  const [telegramBotToken, setTelegramBotToken] = useState("");
  const [telegramDefaultChatId, setTelegramDefaultChatId] = useState("");
  const [telegramNotifyOnRecovery, setTelegramNotifyOnRecovery] = useState(true);
  const [telegramCooldownMinutes, setTelegramCooldownMinutes] = useState(10);
  const [showBotToken, setShowBotToken] = useState(false);
  const [telegramLoading, setTelegramLoading] = useState(true);
  const [telegramSaving, setTelegramSaving] = useState(false);
  const [telegramTesting, setTelegramTesting] = useState(false);
  const [telegramStatus, setTelegramStatus] = useState(null);

  const serverTypesStore = new CustomStore({
    key: 'id',
    load: () => api.get('/servertypes').then(res => res.data),
    insert: (values) => api.post('/servertypes', values).then(res => res.data),
    update: (key, values) => api.put(`/servertypes/${key}`, { id: key, ...values }).then(res => res.data),
    remove: (key) => api.delete(`/servertypes/${key}`).then(res => res.data)
  });

  const loadGeneral = useCallback(async () => {
    setLoading(true);
    setStatus(null);
    try {
      const data = await settingsService.getCAGlobalSettings();
      setAllowAutoCreate(data.allowAutoCreate ?? true);
    } catch {
      setStatus({ type: "error", message: "Failed to load general settings. Check your connection." });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTelegram = useCallback(async () => {
    setTelegramLoading(true);
    setTelegramStatus(null);
    try {
      const data = await settingsService.getTelegramSettings();
      setTelegramEnabled(data.enabled ?? false);
      setTelegramBotToken(data.botToken ?? "");
      setTelegramDefaultChatId(data.defaultChatId ?? "");
      setTelegramNotifyOnRecovery(data.notifyOnRecovery ?? true);
      setTelegramCooldownMinutes(data.cooldownMinutes ?? 10);
    } catch {
      setTelegramStatus({ type: "error", message: "Failed to load Telegram settings. Check your connection." });
    } finally {
      setTelegramLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGeneral();
    loadTelegram();
  }, [loadGeneral, loadTelegram]);

  const handleSaveGeneral = async () => {
    setSaving(true);
    setStatus(null);
    try {
      await settingsService.setCAGlobalSettings({ allowAutoCreate });
      setStatus({ type: "success", message: "Settings saved successfully." });
    } catch {
      setStatus({ type: "error", message: "Failed to save settings. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTelegram = async () => {
    setTelegramSaving(true);
    setTelegramStatus(null);
    try {
      await settingsService.setTelegramSettings({
        enabled: telegramEnabled,
        botToken: telegramBotToken,
        defaultChatId: telegramDefaultChatId,
        notifyOnRecovery: telegramNotifyOnRecovery,
        cooldownMinutes: parseInt(telegramCooldownMinutes, 10) || 10
      });
      setTelegramStatus({ type: "success", message: "Telegram notification settings saved successfully." });
    } catch {
      setTelegramStatus({ type: "error", message: "Failed to save Telegram settings. Please try again." });
    } finally {
      setTelegramSaving(false);
    }
  };

  const handleTestTelegram = async () => {
    if (!telegramBotToken) {
      setTelegramStatus({ type: "error", message: "Please enter a Bot Token before testing." });
      return;
    }
    if (!telegramDefaultChatId) {
      setTelegramStatus({ type: "error", message: "Please enter a Default Chat ID before testing." });
      return;
    }

    setTelegramTesting(true);
    setTelegramStatus(null);
    try {
      const res = await settingsService.testTelegramSettings({
        botToken: telegramBotToken,
        chatId: telegramDefaultChatId
      });
      setTelegramStatus({ type: "success", message: res.message || "Test message delivered successfully!" });
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Failed to send test message. Check your bot token and chat ID.";
      setTelegramStatus({ type: "error", message: errorMsg });
    } finally {
      setTelegramTesting(false);
    }
  };

  return (
    <div className="portal-stack" style={{ maxWidth: "760px" }}>

      {/* Page header */}
      <div className="dashboard-header" style={{ marginBottom: "16px" }}>
        <div className="dashboard-title-area">
          <h1>Settings</h1>
          <p>Configure system behaviour, alerts, and integration parameters.</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: "16px", marginBottom: "24px", borderBottom: "1px solid var(--border-color)" }}>
        <button
          onClick={() => setActiveTab("general")}
          style={{
            background: "none",
            border: "none",
            borderBottom: activeTab === "general" ? "2px solid var(--brand-light)" : "2px solid transparent",
            color: activeTab === "general" ? "var(--brand-light)" : "var(--text-muted)",
            padding: "8px 16px",
            fontWeight: "600",
            cursor: "pointer"
          }}
        >
          General Integrations
        </button>
        <button
          onClick={() => setActiveTab("telegram")}
          style={{
            background: "none",
            border: "none",
            borderBottom: activeTab === "telegram" ? "2px solid var(--brand-light)" : "2px solid transparent",
            color: activeTab === "telegram" ? "var(--brand-light)" : "var(--text-muted)",
            padding: "8px 16px",
            fontWeight: "600",
            cursor: "pointer"
          }}
        >
          Telegram Notifications
        </button>
        <button
          onClick={() => setActiveTab("servertypes")}
          style={{
            background: "none",
            border: "none",
            borderBottom: activeTab === "servertypes" ? "2px solid var(--brand-light)" : "2px solid transparent",
            color: activeTab === "servertypes" ? "var(--brand-light)" : "var(--text-muted)",
            padding: "8px 16px",
            fontWeight: "600",
            cursor: "pointer"
          }}
        >
          Server Types
        </button>
      </div>

      {activeTab === "general" && (
        <div className="portal-panel">
          <div className="portal-panel-header">
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {/* CAGlobal icon */}
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "var(--radius-md)",
                background: "var(--accent-soft)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--brand-light)",
                flexShrink: 0,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <div>
              <h2 className="portal-panel-title" style={{ margin: 0 }}>CAGlobal Integration</h2>
              <span className="portal-panel-hint">Token-based single sign-on settings</span>
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: "24px 0", textAlign: "center", color: "var(--text-muted)", fontSize: "var(--fs-sm)" }}>
            Loading settings…
          </div>
        ) : (
          <div>
            {/* Setting rows */}
            <SettingRow
              id="allowAutoCreate"
              label="Auto-create accounts"
              description={
                allowAutoCreate
                  ? "When a valid CAGlobal token is received for an unknown username, a new Dashboard-only account will be created automatically."
                  : "Auto account creation is disabled. Users must be created manually before they can sign in via CAGlobal."
              }
              badge="Default"
              checked={allowAutoCreate}
              onChange={setAllowAutoCreate}
              disabled={saving}
            />

            {/* Informational note — last row, no border */}
            <div style={{ padding: "18px 0 4px 0" }}>
              <div
                style={{
                  background: "var(--accent-soft)",
                  border: "1px solid var(--accent-glow)",
                  borderRadius: "var(--radius-md)",
                  padding: "12px 14px",
                  display: "flex",
                  gap: "10px",
                  alignItems: "flex-start",
                }}
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--brand-light)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ flexShrink: 0, marginTop: "1px" }}
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <p style={{ margin: 0, fontSize: "var(--fs-xs)", color: "var(--text-muted)", lineHeight: 1.6 }}>
                  Auto-created accounts receive the <strong style={{ color: "var(--text-color)" }}>CAGlobal</strong> role —
                  Dashboard access only, no sidebar, no Packages or Logs. The shared secret token is configured
                  in <code style={{ fontFamily: "var(--font-mono)", fontSize: "0.7rem" }}>appsettings.json → CAGlobal:SharedSecret</code>.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Status feedback */}
        {status && (
          <div style={{ marginTop: "12px" }}>
            <StatusBanner type={status.type} message={status.message} />
          </div>
        )}

        {/* Save button */}
        {!loading && (
          <div style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
            <button
              id="settings-discard-btn"
              className="action-btn"
              onClick={loadGeneral}
              disabled={saving}
              style={{ minWidth: "80px" }}
            >
              Discard
            </button>
            <button
              id="settings-save-btn"
              className="action-btn"
              onClick={handleSaveGeneral}
              disabled={saving}
              style={{
                minWidth: "100px",
                background: "var(--brand-mid)",
                borderColor: "transparent",
                color: "#fff",
                fontWeight: 600,
              }}
            >
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        )}
      </div>
      )}

      {activeTab === "telegram" && (
        <div className="portal-panel">
          <div className="portal-panel-header">
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {/* Telegram icon */}
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "var(--radius-md)",
                  background: "var(--accent-soft)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--brand-light)",
                  flexShrink: 0,
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </div>
              <div>
                <h2 className="portal-panel-title" style={{ margin: 0 }}>Telegram Alert System</h2>
                <span className="portal-panel-hint">Configure real-time push alerts for server outages and service recovery</span>
              </div>
            </div>
          </div>

          {telegramLoading ? (
            <div style={{ padding: "24px 0", textAlign: "center", color: "var(--text-muted)", fontSize: "var(--fs-sm)" }}>
              Loading Telegram settings…
            </div>
          ) : (
            <div>
              {/* Enable alerts toggle */}
              <SettingRow
                id="telegramEnabled"
                label="Enable Telegram Notifications"
                description="Automatically dispatch instant Telegram notifications when any server service misses heartbeats and transitions to Offline."
                checked={telegramEnabled}
                onChange={setTelegramEnabled}
                disabled={telegramSaving}
              />

              {/* Bot Token Input */}
              <div style={{ padding: "18px 0", borderBottom: "1px solid var(--border-color)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                  <label htmlFor="telegramBotToken" style={{ fontSize: "var(--fs-base)", fontWeight: 600, color: "var(--text-color)" }}>
                    Telegram Bot Token
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowBotToken(!showBotToken)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--brand-light)",
                      fontSize: "var(--fs-xs)",
                      cursor: "pointer",
                      fontWeight: 600
                    }}
                  >
                    {showBotToken ? "Hide Token" : "Show Token"}
                  </button>
                </div>
                <p style={{ fontSize: "var(--fs-sm)", color: "var(--text-muted)", margin: "0 0 10px 0" }}>
                  The HTTP API token generated by <code>@BotFather</code> for your alert bot.
                </p>
                <input
                  id="telegramBotToken"
                  type={showBotToken ? "text" : "password"}
                  value={telegramBotToken}
                  onChange={(e) => setTelegramBotToken(e.target.value)}
                  placeholder="e.g. 123456789:AAET4h1O0TGm66KUll3pkorhjztbnVL2-lU"
                  disabled={telegramSaving}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: "var(--radius-md)",
                    border: "1px solid var(--border-color)",
                    background: "var(--bg-card)",
                    color: "var(--text-color)",
                    fontSize: "var(--fs-sm)",
                    fontFamily: "var(--font-mono)",
                    boxSizing: "border-box"
                  }}
                />
              </div>

              {/* Default Chat / Channel ID */}
              <div style={{ padding: "18px 0", borderBottom: "1px solid var(--border-color)" }}>
                <label htmlFor="telegramDefaultChatId" style={{ display: "block", fontSize: "var(--fs-base)", fontWeight: 600, color: "var(--text-color)", marginBottom: "6px" }}>
                  Default Channel / Group Chat ID
                </label>
                <p style={{ fontSize: "var(--fs-sm)", color: "var(--text-muted)", margin: "0 0 10px 0" }}>
                  The primary group or channel chat ID (e.g. <code>-1001234567890</code> or <code>-584211563</code>) where server alerts will be delivered.
                </p>
                <input
                  id="telegramDefaultChatId"
                  type="text"
                  value={telegramDefaultChatId}
                  onChange={(e) => setTelegramDefaultChatId(e.target.value)}
                  placeholder="e.g. -584211563"
                  disabled={telegramSaving}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: "var(--radius-md)",
                    border: "1px solid var(--border-color)",
                    background: "var(--bg-card)",
                    color: "var(--text-color)",
                    fontSize: "var(--fs-sm)",
                    fontFamily: "var(--font-mono)",
                    boxSizing: "border-box"
                  }}
                />
              </div>

              {/* Notify on Recovery */}
              <SettingRow
                id="telegramNotifyOnRecovery"
                label="Notify on Service Recovery"
                description="Send a notification when an offline server reconnects and resumes sending heartbeats."
                checked={telegramNotifyOnRecovery}
                onChange={setTelegramNotifyOnRecovery}
                disabled={telegramSaving}
              />

              {/* Cooldown minutes */}
              <div style={{ padding: "18px 0", borderBottom: "1px solid var(--border-color)" }}>
                <label htmlFor="telegramCooldown" style={{ display: "block", fontSize: "var(--fs-base)", fontWeight: 600, color: "var(--text-color)", marginBottom: "6px" }}>
                  Alert Suppression Cooldown (Minutes)
                </label>
                <p style={{ fontSize: "var(--fs-sm)", color: "var(--text-muted)", margin: "0 0 10px 0" }}>
                  Suppresses repeated offline alerts for the same server during intermittent flapping. Default is 10 minutes.
                </p>
                <input
                  id="telegramCooldown"
                  type="number"
                  min="1"
                  max="1440"
                  value={telegramCooldownMinutes}
                  onChange={(e) => setTelegramCooldownMinutes(e.target.value)}
                  disabled={telegramSaving}
                  style={{
                    width: "120px",
                    padding: "8px 12px",
                    borderRadius: "var(--radius-md)",
                    border: "1px solid var(--border-color)",
                    background: "var(--bg-card)",
                    color: "var(--text-color)",
                    fontSize: "var(--fs-sm)",
                    boxSizing: "border-box"
                  }}
                />
              </div>

              {/* User Recipient Info Note */}
              <div style={{ padding: "18px 0 4px 0" }}>
                <div
                  style={{
                    background: "var(--accent-soft)",
                    border: "1px solid var(--accent-glow)",
                    borderRadius: "var(--radius-md)",
                    padding: "12px 14px",
                    display: "flex",
                    gap: "10px",
                    alignItems: "flex-start",
                  }}
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--brand-light)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ flexShrink: 0, marginTop: "1px" }}
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <p style={{ margin: 0, fontSize: "var(--fs-xs)", color: "var(--text-muted)", lineHeight: 1.6 }}>
                    In addition to the Default Chat ID, any active user in <strong style={{ color: "var(--text-color)" }}>User Management</strong> with <strong style={{ color: "var(--text-color)" }}>Telegram Alerts</strong> checked and a valid <strong style={{ color: "var(--text-color)" }}>Telegram ID</strong> will also receive direct alert messages.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Telegram Status feedback */}
          {telegramStatus && (
            <div style={{ marginTop: "12px" }}>
              <StatusBanner type={telegramStatus.type} message={telegramStatus.message} />
            </div>
          )}

          {/* Telegram Action buttons */}
          {!telegramLoading && (
            <div style={{ marginTop: "20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <button
                id="telegram-test-btn"
                className="action-btn"
                onClick={handleTestTelegram}
                disabled={telegramSaving || telegramTesting}
                style={{
                  minWidth: "140px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
                {telegramTesting ? "Sending Test…" : "Send Test Alert"}
              </button>

              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  id="telegram-discard-btn"
                  className="action-btn"
                  onClick={loadTelegram}
                  disabled={telegramSaving || telegramTesting}
                  style={{ minWidth: "80px" }}
                >
                  Discard
                </button>
                <button
                  id="telegram-save-btn"
                  className="action-btn"
                  onClick={handleSaveTelegram}
                  disabled={telegramSaving || telegramTesting}
                  style={{
                    minWidth: "100px",
                    background: "var(--brand-mid)",
                    borderColor: "transparent",
                    color: "#fff",
                    fontWeight: 600,
                  }}
                >
                  {telegramSaving ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "servertypes" && (
        <div className="portal-panel" style={{ padding: "0" }}>
          <DataGrid
            dataSource={serverTypesStore}
            showBorders={false}
            rowAlternationEnabled={true}
            hoverStateEnabled={true}
            columnAutoWidth={true}
            width="100%"
          >
            <Editing
              mode="popup"
              allowUpdating={true}
              allowAdding={true}
              allowDeleting={true}
              useIcons={true}
            >
              <Popup title="Server Type Details" showTitle={true} width={400} height={250} />
              <Form>
                <Item itemType="group" colCount={1}>
                  <Item dataField="name" isRequired={true} />
                  <Item dataField="sftpFolder" isRequired={true} />
                </Item>
              </Form>
            </Editing>
            
            <Column dataField="id" caption="ID" allowEditing={false} width={80} alignment="center" />
            <Column dataField="name" caption="Type Name" validationRules={[{ type: 'required' }]} />
            <Column dataField="sftpFolder" caption="SFTP Folder" validationRules={[{ type: 'required' }]} />
            
            <Paging defaultPageSize={10} />
            <Pager showPageSizeSelector={true} allowedPageSizes={[5, 10, 20]} showInfo={true} />
          </DataGrid>
        </div>
      )}
    </div>
  );
}
